import classNames from "classnames";
import { motion } from "framer-motion";
import { useState } from "react";
import { ModalComponent } from "../..";
import { useModalControl } from "../../../../customHooks/useModalControl";
import {
  formatToCurrency,
  formatToNumber,
  getOffsetFromPage,
  getPageFromOffset,
  parseStringToNumber,
} from "../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../redux/features/cooperative/cooperative.type";
import {
  useFetchFailedDisbursementsQuery,
  useRetryFailedDisbursementsMutation,
} from "../../../../redux/features/loans/loans.api";
import { FailedDisbursementsQuery } from "../../../../redux/features/loans/loans.type";
import { ApprovalActionDetails } from "../../../../utils/types";
import { ApprovalButtonSet } from "../../../buttons/approvalButtonSet";
import { InformationCard } from "../../../cards/infoCard";
import { TableComponent } from "../../../table/table";
import { doNotification } from "../../../toast/nofitication";
import { ConfirmationModal } from "../confirmationModal";

export const FailedDisbursementsModal = ({
  isModalOpen,
  onModalClose,
  cooperativeDetails,
  onSuccess,
}: {
  isModalOpen: boolean;
  onModalClose: () => void;
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: () => void;
}) => {
  const defaultQuery: FailedDisbursementsQuery = {
    offset: 0,
    limit: 20,
    text: "",
    coop: cooperativeDetails.id,
    logs: true,
  };

  const [query, setQuery] = useState<FailedDisbursementsQuery>(defaultQuery);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [actionDetails, setActionDetails] = useState<ApprovalActionDetails>();

  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const { data: failedDisbursements, isFetching: fetchingFailedDisbursements } =
    useFetchFailedDisbursementsQuery(query, {
      refetchOnMountOrArgChange: true,
    });

  const [doRetry, { isLoading: laodingRetry }] =
    useRetryFailedDisbursementsMutation();

  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen}
        onModalClose={() => {
          onModalClose();
        }}
        title="Failed Loan Disbursements"
        topTitle
        subTitle=""
        childrenClassName="flex flex-col gap-4"
        modalClassName="!min-h-[572px] !max-w-[1038px]"
        hasButtons={false}
      >
        <InformationCard
          info={
            <span className="flex gap-1 md:gap-10 flex-wrap">
              <span>
                Cooperative balance:{" "}
                <span className="text-info-purple font-bold">
                  {formatToCurrency(
                    failedDisbursements?.data?.totalInCoop ?? 0,
                    { currency: "NGN", decimalPlaces: 2 }
                  )}
                </span>
              </span>

              <span>
                Total failed disbursement value:{" "}
                <span className="text-info-purple font-bold">
                  {formatToCurrency(failedDisbursements?.data?.totalLoan ?? 0, {
                    currency: "NGN",
                    decimalPlaces: 2,
                  })}
                </span>
              </span>
            </span>
          }
          size="mini"
          variant="purple"
          className={"!items-center"}
        />

        {selectedIds.length > 0 ? (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={classNames(
              "md:py-0 z-10 py-4",
              "flex items-center gap-4 justify-between flex-row overflow-hidden",
              {
                "max-md:gap-0": selectedIds.length > 0,
              }
            )}
          >
            <ApprovalButtonSet
              onReject={async () => {
                setActionDetails({
                  type: "multi",
                  isApprove: false,
                });

                handleModalOpen("confirmationModal");
              }}
              onAccept={async () => {
                setActionDetails({
                  type: "multi",
                  isApprove: true,
                });

                handleModalOpen("confirmationModal");
              }}
              onClear={() => {
                setSelectedIds([]);
              }}
              acceptText={`Approve (${selectedIds.length})`}
              rejectText={`Decline (${selectedIds.length})`}
              className={classNames(
                "ml-auto overflow-hidden max-md:w-full w-fit"
              )}
              disableActions={laodingRetry}
            />
          </motion.div>
        ) : undefined}

        <TableComponent
          mobileCardView={false}
          headers={["Member", "Loan Type", "Loan Amount", "Tenor"]}
          headerClassName="!bg-white"
          headerActionProps={{
            hasActions: false,
            hasCheckbox: true,
            checked:
              selectedIds.length > 0 &&
              selectedIds.length ===
                failedDisbursements?.data?.logs?.results?.length,
            onCheck: () => {
              setSelectedIds(
                failedDisbursements?.data?.logs?.results?.map((i) => i.id) ?? []
              );
            },
            onUncheck: () => {
              setSelectedIds([]);
            },
            hasMoreMenu: false,
          }}
          rows={
            failedDisbursements?.data?.logs?.results?.map((entry, idx) => {
              return {
                id: entry.id,
                onClick: () => {
                  if (selectedIds.includes(entry.id)) {
                    setSelectedIds(selectedIds.filter((i) => i !== entry.id));
                  } else {
                    setSelectedIds([...selectedIds, entry.id]);
                  }
                },
                items: [
                  <p className="min-w-fit whitespace-nowrap">
                    {entry?.MemberID?.User?.FirstName}{" "}
                    {entry?.MemberID?.User?.LastName}
                  </p>,
                  entry?.LoanProduct?.LoanName,
                  formatToCurrency(entry?.Amount, {
                    decimalPlaces: 2,
                    currency: "NGN",
                  }),
                  `${formatToNumber(entry?.Tenure, {
                    decimalPlaces: 0,
                  })} ${
                    parseStringToNumber(entry?.Tenure) === 1
                      ? "month"
                      : "months"
                  }`,
                ],
                actionProps: {
                  hasActions: false,
                  hasCheckbox: true,
                  checked: selectedIds.includes(entry.id),
                  onCheck: () => {},
                  onUncheck: () => {},
                  hasMoreMenu: false,
                  moreOptions: [],
                },
              };
            }) ?? []
          }
          stickyLastCol={false}
          isRowClickable={true}
          onRowClick={() => {}}
          isTableEmpty={
            ![0, undefined].includes(
              failedDisbursements?.data?.logs?.results?.length
            )
          }
          emptyTitle="No Failed Loans"
          isLoading={fetchingFailedDisbursements}
          isPaginated
          currentPage={getPageFromOffset(query.offset!, query.limit!)}
          totalCount={failedDisbursements?.data?.logs?.count}
          itemsPerPage={query.limit}
          onPageChange={(page) => {
            setQuery({
              ...query,
              offset: getOffsetFromPage(page, query.limit!),
            });
            setSelectedIds([]);
          }}
        />
      </ModalComponent>

      {!!actionDetails ? (
        <ConfirmationModal
          cancelButtonText="Close"
          isModalOpen={modalOpen && modal === "confirmationModal"}
          onModalClose={() => {
            handleModalClose();
          }}
          title={`Confirm ${actionDetails?.isApprove ? "Retry" : "Cancel"}`}
          subtitle={
            <>
              {actionDetails.type === "multi" ? (
                <span>
                  You're about to {actionDetails.isApprove ? "retry" : "cancel"}{" "}
                  a total of{" "}
                  <span className="text-pry-green-200 font-medium">
                    {selectedIds.length} selected
                  </span>{" "}
                  loan disbursements.
                </span>
              ) : undefined}
            </>
          }
          loading={laodingRetry}
          proceedButtonText={
            actionDetails?.isApprove ? "Yes, Retry" : "Yes, Cancel"
          }
          onProceed={async () => {
            if (actionDetails?.type === "multi") {
              const resp = await doRetry({
                coop: cooperativeDetails.id,
                logs: selectedIds.map((id) => {
                  return {
                    id,
                    answer: actionDetails.isApprove,
                  };
                }),
              });

              if (resp.data?.success) {
                doNotification("Operation successful", "success");
                handleModalClose();
                setSelectedIds([]);
                onSuccess();
              }
            }
          }}
        />
      ) : undefined}
    </>
  );
};
